import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Home, FolderCog, Settings, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const baseNavItems = [
  { path: '/dashboard', label: 'Overview', icon: LayoutDashboard },
  { path: '/households', label: 'Households', icon: Home },
  { path: '/configurations', label: 'Configurations', icon: FolderCog },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const adminNavItem = { path: '/admin/households', label: 'Admin', icon: ShieldCheck };

export const AppLayout = ({ user, children, activeNav }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = activeNav || location.pathname;
  const navItems = user?.is_super_admin ? [...baseNavItems, adminNavItem] : baseNavItems;

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (e) {
      console.error('Logout error:', e);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] overflow-x-hidden">
      {/* Header — clean, minimal, matching screenshot style */}
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2.5 hover:opacity-80 transition">
              <img src="/logo.png" alt="Tell M" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-heading font-extrabold text-stone-900">Tell M <span className="text-stone-400 font-medium">|</span> <span className="text-stone-600 font-semibold">Invoices</span></span>
            </button>

            <div className="flex items-center gap-1">
              {navItems.map(item => {
                const isActive = currentPath.startsWith(item.path);
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`px-3.5 py-2 text-sm font-medium flex items-center gap-1.5 rounded-lg transition ${
                      isActive
                        ? 'text-[#4F6F52] bg-[#4F6F52]/8'
                        : 'text-stone-400 hover:text-stone-600 hover:bg-stone-50'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{item.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              {user?.picture && (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              )}
              <span className="text-sm font-medium text-stone-500 hidden md:inline">{user?.name}</span>
              <button
                onClick={handleLogout}
                className="text-sm text-stone-400 hover:text-stone-600 px-3 py-1.5 rounded-lg hover:bg-stone-50 transition"
                data-testid="logout-button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        {children}
      </main>
    </div>
  );
};
